import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDashboardPage from "@/pages/ClientDashboardPage";
import LoginPage from "@/pages/LoginPage";
import StaffPage from "@/pages/StaffPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ReportsPage from "@/pages/ReportsPage";
import ContentManagerPage from "@/pages/ContentManagerPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import "@/App.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("App error:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="ab-card ab-shadow-sm max-w-md p-6">
            <h2 className="text-base font-semibold text-[#0F172A]">
              Something went wrong
            </h2>
            <p className="mt-2 text-sm text-[#64748B]">
              An unexpected error occurred while rendering the app. Reload to
              try again.
            </p>
            <button
              className="mt-4 inline-flex items-center rounded-md border border-[#E2E8F0] bg-white px-3 py-1.5 text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC]"
              onClick={() => window.location.reload()}
              data-testid="error-reload-btn"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-[#F8F9FA] text-[#0F172A]">
        {!online && (
          <div
            className="w-full bg-[#FEF3C7] text-[#92400E] text-xs px-4 py-1.5 text-center border-b border-[#F59E0B]/30"
            data-testid="offline-banner"
          >
            You are offline. Changes will resume when the network is restored.
          </div>
        )}
        <Routes>
          {/* Public marketing landing page */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/change-password"
            element={
              <RequireAuth allowMustChange>
                <ChangePasswordPage />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <AppShell>
                  <ClientsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/clients/:clientId"
            element={
              <RequireAuth>
                <AppShell>
                  <ClientDashboardPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/staff"
            element={
              <RequireAuth roles={["admin"]}>
                <AppShell>
                  <StaffPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/reports"
            element={
              <RequireAuth roles={["admin"]}>
                <AppShell>
                  <ReportsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/content"
            element={
              <RequireAuth roles={["admin"]}>
                <AppShell>
                  <ContentManagerPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Public blog routes */}
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              color: "#0F172A",
              fontSize: "13px",
            },
          }}
        />
      </div>
    </ErrorBoundary>
  );
}

function RequireAuth({ children, roles, allowMustChange = false }) {
  const { isAuthed, user } = useAuth();
  const location = useLocation();
  if (!isAuthed) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Force first-login password change before anything else.
  if (user?.mustChangePassword && !allowMustChange) {
    return <Navigate to="/change-password" replace />;
  }
  if (roles && user && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}
